import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  // Avatar,  // Commented out - used in avatar section
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Description,
  ContactPage,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  ContactPage as ContactPageIcon,
} from '@mui/icons-material';
import CollapsibleBaseBlock from './CollapsibleBaseBlock';
import { useSubjectLanguageVariants, useUserState } from '@features/user';
import {
  usePatientForms,
  useFormDetail,
  // useAssignForms, // Now handled in ReviewFormsSlidePane
  useDeleteForm,
  useForms,
  useCloneForm,
} from '@features/forms';
import type { Form, ExtendedFormDefinition } from '@features/forms';
import type { FormWithRevisionSummary } from '@store/generated/forms';
import { Patient } from '../../../types/types';
import debounce from 'lodash/debounce';
import { capitalize, formatDate } from '../../../utils/utils';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// import Logo from '../../../components/Icons/Logo';  // Commented out - used in avatar section
import { useModal } from '@store/hooks';
import { MODAL_TYPES } from '../../../components/modals';
import { useStickyHeaderDetection } from '../../../hooks/useStickyHeaderDetection';
import FormDetailsSlidePane from '../../Forms/FormDetailsSlidePane';
import FormPreviewSlidePane from '../../Forms/FormPreviewSlidePane';
import FormSelectorSlidePane from '../../Forms/FormSelectorSlidePane';
import ReviewFormsSlidePane from '../../Forms/ReviewFormsSlidePane';

interface FormsListBlockProps {
  patientId: string;
  patient?: Patient;
  defaultCollapsed?: boolean;
  /** Render inside a page-owned section without duplicate card/title chrome. */
  embedded?: boolean;
}

/**
 * Adapter function to transform V2 FormWithRevisionSummary to legacy ExtendedFormDefinition
 * This maintains compatibility with existing form selection UI
 */
function transformV2FormsToLegacy(v2Forms: FormWithRevisionSummary[]): ExtendedFormDefinition[] {
  return v2Forms.map(item => {
    const { form, current_revision } = item;

    // Determine form type based on owner_scope
    let type: 'public' | 'user' | 'group' = 'user';
    if (form.owner_scope === 'public') {
      type = 'public';
    } else if (form.owner_scope === 'org') {
      type = 'group';
    }

    return {
      form_id: form.id || '',
      title: current_revision?.title || 'Untitled Form',
      description: current_revision?.description || '',
      validationSchema: undefined, // Not available in V2 summary
      groups: [], // Not available in V2 summary - will be loaded for preview
      type,
      form_type: form.form_type as 'standard' | 'test',
    };
  });
}

const FormsListBlock: React.FC<FormsListBlockProps> = ({
  patientId,
  patient,
  defaultCollapsed = false,
  embedded = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { singular: subjectLanguage, singularCap: subjectLanguageCap } =
    useSubjectLanguageVariants();
  const userState = useUserState();
  const [isFormSelectorOpen, setIsFormSelectorOpen] = useState(false);
  const [selectedFormIds, setSelectedFormIds] = useState<string[]>([]);
  const [formListPage, setFormListPage] = useState(1);
  const FORMS_PER_PAGE = 100; // TODO: lower once BE pagination is fixed
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [isFormDetailsOpen, setIsFormDetailsOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(!defaultCollapsed);
  const navigate = useNavigate();
  const [previewFormId, setPreviewFormId] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isCloningForm, setIsCloningForm] = useState(false);
  const [isReviewPaneOpen, setIsReviewPaneOpen] = useState(false);
  // const userInfo = getUserInfoFromLocalStorage();  // Commented out - used in avatar section
  const { open: openModal } = useModal();
  const { isStuck, registerSentinel, registerHeader } = useStickyHeaderDetection();

  const {
    data: forms,
    total: totalRows,
    isLoading: loading,
  } = usePatientForms(
    {
      patient_id: patientId,
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      status: statusFilter === 'completed' ? 'completed' : undefined,
      exclude_completed: statusFilter === 'open' ? true : undefined,
    },
    {
      skip: !hasLoadedData,
      refetchOnFocus: true,
    },
  );

  const { data: formDetail, isLoading: isLoadingDetail } = useFormDetail(
    { form_session_id: selectedForm?.form_session_id || '' },
    { skip: !selectedForm?.form_session_id },
  );

  const {
    forms: rawV2Forms,
    totalPages: totalFormPages,
    refetch: refetchAvailableForms,
  } = useForms(
    {
      page: formListPage,
      pageSize: FORMS_PER_PAGE,
      search: searchTerm || undefined,
      // includeDrafts: false (default - no drafts as requested by user)
    },
    {
      skip: !isFormSelectorOpen && forms.length > 0,
    },
  );

  // Transform V2 forms to legacy format for compatibility
  const availableForms = transformV2FormsToLegacy(rawV2Forms);

  // Group forms by scope
  const groupedForms = useMemo(() => {
    const groups = {
      provider: [] as typeof availableForms,
      org: [] as typeof availableForms,
      public: [] as typeof availableForms,
    };

    availableForms.forEach(form => {
      const formType = form.type;
      if (formType === 'user') {
        groups.provider.push(form);
      } else if (formType === 'group') {
        groups.org.push(form);
      } else if (formType === 'public') {
        groups.public.push(form);
      }
    });

    return groups;
  }, [availableForms]);

  // const { mutate: assignForms } = useAssignForms(); // Now handled in ReviewFormsSlidePane
  const { mutate: deleteForm } = useDeleteForm();
  const { mutate: cloneForm } = useCloneForm();

  useEffect(() => {
    if (!defaultCollapsed) {
      setHasLoadedData(true);
    }
  }, []);

  // totalFormPages is now provided directly by the V2 API

  const handleFormClick = (form: Form) => {
    setSelectedForm(form);
    setIsFormDetailsOpen(true);
  };

  const handleFormDetailsClose = () => {
    setIsFormDetailsOpen(false);
    setSelectedForm(null);
    window.history.replaceState(null, '', window.location.pathname);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        setFormListPage(1);
        refetchAvailableForms();
      }, 500),
    [refetchAvailableForms],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedSearch();
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    debouncedSearch.cancel();
    setFormListPage(1);
    refetchAvailableForms();
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleAddForm = () => {
    // Check if the onboarding step is completed
    if (!userState?.onboardingSteps?.patient_communication_sms_onboarding_completed) {
      openModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {});
      return;
    }

    setIsFormSelectorOpen(true);
    setFormListPage(1);
  };

  const handleFormSelect = (formId: string) => {
    setSelectedFormIds(prev =>
      prev.includes(formId) ? prev.filter(id => id !== formId) : [...prev, formId],
    );
  };

  const handleCreateFormSession = () => {
    setIsReviewPaneOpen(true);
  };

  const handleFormListPageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setFormListPage(page);
  };

  const handleDeleteForm = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmDeleteForm = async (formSessionId: string): Promise<void> => {
    if (!formSessionId) return;

    return new Promise<void>((resolve, reject) => {
      deleteForm(
        { form_session_id: formSessionId },
        {
          onSuccess: () => {
            enqueueSnackbar('Form deleted successfully', { variant: 'success' });
            setIsFormDetailsOpen(false);
            setSelectedForm(null);
            // Note: No need to manually refetch - RTK Query cache invalidation handles this automatically
            handleCloseDeleteDialog();
            resolve();
          },
          onError: error => {
            console.error('Error deleting form:', error);
            enqueueSnackbar('Failed to delete form', { variant: 'error' });
            handleCloseDeleteDialog();
            reject(error);
          },
        },
      );
    });
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const renderStatusChip = (status: string) => (
    <Chip
      label={
        status === 'completed' ? 'Completed' : status === 'in_progress' ? 'In Progress' : 'Pending'
      }
      color={status === 'completed' ? 'success' : status === 'in_progress' ? 'primary' : 'warning'}
      size="small"
    />
  );

  const handleCollapseChange = (isCollapsed: boolean) => {
    if (!isCollapsed && !hasLoadedData) {
      setHasLoadedData(true);
    }
  };

  const handlePreviewForm = (formId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setPreviewFormId(formId);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setPreviewFormId(null);
  };

  const handleSuggestedFormClick = (formId: string) => {
    // Check if the onboarding step is completed
    if (!userState?.onboardingSteps?.patient_communication_sms_onboarding_completed) {
      openModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {});
      return;
    }
    setSelectedFormIds([formId]);
    setIsFormSelectorOpen(true);
    setFormListPage(1);
  };

  const handleSeeAllClick = () => {
    // Check if the onboarding step is completed
    if (!userState?.onboardingSteps?.patient_communication_sms_onboarding_completed) {
      openModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {});
      return;
    }
    setSelectedFormIds([]);
    setIsFormSelectorOpen(true);
    setFormListPage(1);
  };

  const handleCustomizeForm = async () => {
    if (!previewFormId) return;

    setIsCloningForm(true);
    try {
      await cloneForm(
        previewFormId,
        { new_scope: 'provider' },
        {
          onSuccess: data => {
            const newFormId = data?.form?.form?.id;
            if (newFormId) {
              setIsPreviewOpen(false);
              navigate(`/form-builder/${newFormId}`);
            } else {
              enqueueSnackbar('Failed to clone form - no form ID returned', { variant: 'error' });
            }
          },
          onError: error => {
            console.error('Error cloning form:', error);
            enqueueSnackbar('Failed to customize form', { variant: 'error' });
          },
        },
      );
    } catch (error) {
      console.error('Error cloning form:', error);
      enqueueSnackbar('Failed to customize form', { variant: 'error' });
    } finally {
      setIsCloningForm(false);
    }
  };

  return (
    <>
      <CollapsibleBaseBlock
        defaultCollapsed={defaultCollapsed}
        onCollapseChange={handleCollapseChange}
        embedded={embedded}
      >
        {!embedded && (
          <CollapsibleBaseBlock.Header
            title={`${subjectLanguageCap} Forms`}
            icon={<ContactPageIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {forms.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 150, width: 'auto' }}>
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      id="status-filter"
                      value={statusFilter}
                      label="Status"
                      onChange={handleStatusFilterChange}
                      sx={{
                        borderRadius: '0.5rem',
                        width: 'auto',
                        backgroundColor: 'background.paper',
                        height: 32,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'borderColors.primary',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      }}
                      MenuProps={{
                        slotProps: {
                          paper: {
                            sx: {
                              borderRadius: '0.5rem',
                              mt: 0.5,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddForm}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Send Form
                </Button>

                {!userState?.onboardingSteps?.patient_communication_sms_onboarding_completed && (
                  <Tooltip title="Learn about sending forms" arrow>
                    <IconButton
                      size="small"
                      onClick={() => openModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {})}
                      sx={{
                        color: 'primary.main',
                        backgroundColor: 'transparent',
                        borderRadius: '50%',
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            }
          />
        )}

        <CollapsibleBaseBlock.Body>
          {embedded && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              {forms.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="embedded-status-filter-label">Status</InputLabel>
                  <Select
                    labelId="embedded-status-filter-label"
                    value={statusFilter}
                    label="Status"
                    onChange={handleStatusFilterChange}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddForm}
              >
                Send form
              </Button>
              {!userState?.onboardingSteps?.patient_communication_sms_onboarding_completed && (
                <Tooltip title="Learn about sending forms" arrow>
                  <IconButton
                    size="small"
                    aria-label="Learn about sending forms"
                    onClick={() => openModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {})}
                    sx={{
                      color: 'primary.main',
                      p: 0.5,
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          {loading ? (
            <Box sx={{ width: '100%', p: 1 }}>
              <Skeleton
                variant="rectangular"
                sx={{ width: '100%', height: 40, mb: 1, borderRadius: 1 }}
              />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Form Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Assigned Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(3)].map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Skeleton variant="text" sx={{ width: '80%' }} />
                        </TableCell>
                        <TableCell>
                          <Skeleton
                            variant="rectangular"
                            sx={{ width: 80, height: 24, borderRadius: 1 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Skeleton variant="text" sx={{ width: '60%' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Skeleton variant="rectangular" sx={{ width: 250, height: 32, borderRadius: 1 }} />
              </Box>
            </Box>
          ) : forms.length === 0 ? (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                        height: '37px',
                        py: 1,
                      }}
                    >
                      Suggested &mdash; send your {subjectLanguage} one of these forms
                    </TableCell>
                  </TableRow>
                  {availableForms.slice(0, 2).map(form => (
                    <TableRow
                      key={form.form_id}
                      hover
                      onClick={() => handleSuggestedFormClick(form.form_id)}
                      sx={{
                        cursor: 'pointer',
                        '& td': {
                          height: '37px',
                          py: 1,
                        },
                      }}
                    >
                      <TableCell colSpan={3}>{form.title}</TableCell>
                    </TableRow>
                  ))}
                  {availableForms.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        align="center"
                        sx={{
                          color: 'text.secondary',
                          height: '37px',
                          py: 1,
                        }}
                      >
                        No forms available. Click "Send Form" to view forms.
                      </TableCell>
                    </TableRow>
                  )}
                  {availableForms.length > 0 && (
                    <TableRow
                      sx={{
                        '& td': {
                          borderBottom: 'none',
                          height: '37px',
                          py: 1,
                        },
                      }}
                    >
                      <TableCell colSpan={3}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={handleSeeAllClick}
                          underline="always"
                          sx={{
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            padding: 0,
                            font: 'inherit',
                            color: 'primary.main',
                          }}
                        >
                          See All
                        </Link>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Form Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Assigned Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {forms.map(form => (
                      <TableRow
                        key={form.id}
                        hover
                        onClick={() => handleFormClick(form)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{form.title}</TableCell>
                        <TableCell>{renderStatusChip(form.status)}</TableCell>
                        <TableCell>{formatDate(form.created_timestamp)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalRows}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </>
          )}
        </CollapsibleBaseBlock.Body>
      </CollapsibleBaseBlock>

      <FormSelectorSlidePane
        open={isFormSelectorOpen}
        onClose={() => {
          setIsFormSelectorOpen(false);
          setSelectedFormIds([]); // Clear selected forms when closing
        }}
        patient={patient}
        subjectLanguage={subjectLanguage}
        subjectLanguageCap={subjectLanguageCap}
        selectedFormIds={selectedFormIds}
        onFormSelect={handleFormSelect}
        onCreateFormSession={handleCreateFormSession}
        onPreviewForm={formId => {
          setPreviewFormId(formId);
          setIsPreviewOpen(true);
        }}
      />

      <FormDetailsSlidePane
        open={isFormDetailsOpen}
        onClose={handleFormDetailsClose}
        selectedForm={selectedForm}
        formDetail={formDetail as any}
        isLoadingDetail={isLoadingDetail}
        subjectLanguage={subjectLanguage}
        onDeleteForm={handleConfirmDeleteForm}
      />

      <FormPreviewSlidePane
        open={isPreviewOpen}
        onClose={handleClosePreview}
        formId={previewFormId}
        showCustomizeLink={true}
        subjectLanguageCap={subjectLanguageCap}
        isCustomizing={isCloningForm}
        onCustomize={handleCustomizeForm}
      />

      <ReviewFormsSlidePane
        open={isReviewPaneOpen}
        onClose={() => setIsReviewPaneOpen(false)}
        patient={patient}
        patientId={patientId}
        selectedFormIds={selectedFormIds}
        forms={availableForms}
        onSuccess={() => {
          setIsReviewPaneOpen(false);
          setIsFormSelectorOpen(false);
          setSelectedFormIds([]);
          // Note: No need to manually refetch - RTK Query cache invalidation handles this automatically
        }}
      />
    </>
  );
};

export default FormsListBlock;
